import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useProfile, useUpdateProfile } from "@/hooks/useApi";
import { cn } from "@/cn";
import type { ProfilePatch } from "@/types";

export function ProfileOverviewPage() {
  const { data: profile, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();

  const [form, setForm] = useState<ProfilePatch>({});
  const [initialized, setInitialized] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    if (profile && !initialized) {
      setForm({
        name: profile.name ?? "",
        email: profile.email ?? "",
        location: profile.location ?? "",
        targetRoles: profile.targetRoles ?? [],
        compMin: profile.compMin ?? 0,
        compMax: profile.compMax ?? 0,
        currency: profile.currency ?? "USD",
        remote: profile.remote ?? "",
        seniority: profile.seniority ?? "",
        archetypes: profile.archetypes ?? "",
        narrative: profile.narrative ?? "",
        superpowers: profile.superpowers ?? [],
        proofPoints: profile.proofPoints ?? [],
        dealbreakers: profile.dealbreakers ?? [],
      });
      setInitialized(true);
    }
  }, [profile, initialized]);

  const hasChanges =
    initialized &&
    JSON.stringify(form) !== JSON.stringify({
      name: profile?.name ?? "",
      email: profile?.email ?? "",
      location: profile?.location ?? "",
      targetRoles: profile?.targetRoles ?? [],
      compMin: profile?.compMin ?? 0,
      compMax: profile?.compMax ?? 0,
      currency: profile?.currency ?? "USD",
      remote: profile?.remote ?? "",
      seniority: profile?.seniority ?? "",
      archetypes: profile?.archetypes ?? "",
      narrative: profile?.narrative ?? "",
      superpowers: profile?.superpowers ?? [],
      proofPoints: profile?.proofPoints ?? [],
      dealbreakers: profile?.dealbreakers ?? [],
    });

  const targetingLooksEmpty =
    (!(form.targetRoles?.length) &&
      !(form.remote) &&
      !(form.seniority));

  async function handleSave() {
    setSaveStatus("saving");
    try {
      await updateProfile.mutateAsync(form);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }

  function updateField<K extends keyof ProfilePatch>(key: K, value: ProfilePatch[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateArrayField(key: "targetRoles" | "superpowers" | "proofPoints" | "dealbreakers", value: string) {
    setForm((prev) => ({ ...prev, [key]: value.split(",").map((s) => s.trim()).filter(Boolean) }));
  }

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" /></div>;
  }
  if (error) {
    return <p className="text-sm text-destructive">Failed to load profile. Please try again later.</p>;
  }

  return (
    <div className="space-y-6">
      <div
        className="rounded-lg border border-border bg-muted/10 px-4 py-3 text-sm text-muted-foreground"
        data-testid="profile-overview-ai-context-banner"
      >
        Ask AI and evaluation read{" "}
        <Link to="/profile/ai-context" className="font-medium text-brand hover:underline">
          AI Context
        </Link>{" "}
        (and Intelligence). This form is optional structured fields.
      </div>

      <div className="flex items-center justify-end">
        <button
          onClick={handleSave}
          disabled={updateProfile.isPending || !hasChanges}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition",
            saveStatus === "saved" ? "bg-success/20 text-success" : saveStatus === "error" ? "bg-destructive/20 text-destructive" : "bg-brand text-brand-foreground hover:opacity-90",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          {updateProfile.isPending && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />}
          {saveStatus === "saved" ? "Saved!" : saveStatus === "error" ? "Error" : updateProfile.isPending ? "Saving…" : "Save changes"}
        </button>
      </div>

      <Section title="Personal Information">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <input type="text" value={form.name ?? ""} onChange={(e) => updateField("name", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Your full name" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email ?? ""} onChange={(e) => updateField("email", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="you@example.com" />
          </Field>
          <Field label="Location">
            <input type="text" value={form.location ?? ""} onChange={(e) => updateField("location", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="e.g. San Francisco, CA / Remote" />
          </Field>
          <Field label="Remote Preference">
            <select value={form.remote ?? ""} onChange={(e) => updateField("remote", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand">
              <option value="">Select…</option>
              <option value="remote">Remote only</option>
              <option value="hybrid">Hybrid</option>
              <option value="onsite">On-site</option>
              <option value="open">Open to any</option>
            </select>
          </Field>
        </div>
      </Section>

      <Section
        title="Targeting"
        hint={
          targetingLooksEmpty
            ? "Empty here is fine — prefs live in AI Context assistant memory."
            : undefined
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Seniority Level">
            <select value={form.seniority ?? ""} onChange={(e) => updateField("seniority", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand">
              <option value="">Select…</option>
              <option value="entry">Entry / Junior</option>
              <option value="mid">Mid-level</option>
              <option value="senior">Senior</option>
              <option value="lead">Lead / Staff</option>
              <option value="manager">Manager</option>
              <option value="director">Director</option>
              <option value="exec">VP / C-Level</option>
            </select>
          </Field>
          <Field label="Currency">
            <input type="text" value={form.currency ?? "USD"} onChange={(e) => updateField("currency", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="USD" />
          </Field>
          <Field label="Min. Compensation">
            <input type="number" value={form.compMin ?? ""} onChange={(e) => updateField("compMin", Number(e.target.value))} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="e.g. 100000" />
          </Field>
          <Field label="Max. Compensation">
            <input type="number" value={form.compMax ?? ""} onChange={(e) => updateField("compMax", Number(e.target.value))} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="e.g. 200000" />
          </Field>
        </div>
        <Field label="Target Roles (comma-separated)">
          <input type="text" value={(form.targetRoles ?? []).join(", ")} onChange={(e) => updateArrayField("targetRoles", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="AI Engineer, Product Manager, Staff Engineer" />
        </Field>
      </Section>

      <Section title="Narrative & Archetypes">
        <Field label="Archetypes">
          <textarea value={form.archetypes ?? ""} onChange={(e) => updateField("archetypes", e.target.value)} rows={3} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="e.g. Applied AI Builder, Automation Architect" />
        </Field>
        <Field label="Personal Narrative">
          <textarea value={form.narrative ?? ""} onChange={(e) => updateField("narrative", e.target.value)} rows={4} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Your career story and what makes you unique…" />
        </Field>
      </Section>

      <Section title="Lists">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Superpowers (comma-separated)">
            <input type="text" value={(form.superpowers ?? []).join(", ")} onChange={(e) => updateArrayField("superpowers", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Systems thinking, cross-functional leadership" />
          </Field>
          <Field label="Dealbreakers (comma-separated)">
            <input type="text" value={(form.dealbreakers ?? []).join(", ")} onChange={(e) => updateArrayField("dealbreakers", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="No on-site, no legacy tech, no micromanagement" />
          </Field>
        </div>
        <Field label="Proof Points (comma-separated)">
          <input type="text" value={(form.proofPoints ?? []).join(", ")} onChange={(e) => updateArrayField("proofPoints", e.target.value)} className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Built ML pipeline processing 10M+ events/day, Led team of 8 engineers" />
        </Field>
      </Section>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {hint ? (
        <p className="mb-4 text-xs text-muted-foreground">{hint}</p>
      ) : (
        <div className="mb-4" />
      )}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-foreground">{label}</label>
      {children}
    </div>
  );
}